/**
 * @fileoverview User Profile Component
 * @description Displays user information and logout button
 * @module components/auth/UserProfile
 */

import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Divider,
} from '@mui/material';
import { Logout as LogoutIcon } from '@mui/icons-material';
import { useAuth } from '@/context';
import './UserProfile.css';

/**
 * User Profile Component
 * Shows user avatar with dropdown menu for profile actions
 * @returns {JSX.Element} User profile component
 */
export const UserProfile = () => {
  const { isAuthenticated, getUserProfile, logout, isLoading } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const userProfile = getUserProfile();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
  };

  // Don't render if not authenticated
  if (!isAuthenticated || isLoading) {
    return null;
  }

  // Get user initials for avatar
  const getInitials = () => {
    if (!userProfile) return '?';
    if (userProfile.firstName && userProfile.lastName) {
      return `${userProfile.firstName[0]}${userProfile.lastName[0]}`.toUpperCase();
    }
    if (userProfile.name) {
      const parts = userProfile.name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return userProfile.name[0].toUpperCase();
    }
    if (userProfile.email) {
      return userProfile.email[0].toUpperCase();
    }
    return '?';
  };

  return (
    <Box className="user-profile">
      <IconButton
        onClick={handleClick}
        size="small"
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        className="user-profile__button"
      >
        <Avatar className="user-profile__avatar">
          {getInitials()}
        </Avatar>
      </IconButton>
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        disableScrollLock={true}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 4,
            className: 'user-profile__menu',
          },
        }}
      >
        {/* User Info */}
        <Box className="user-profile__info">
          <Typography variant="subtitle1" fontWeight="medium">
            {userProfile?.name || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {userProfile?.email || ''}
          </Typography>
        </Box>
        
        <Divider />

        {/* Logout */}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sign Out</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserProfile;
